import {
  FOC_TOUR_LABEL_IAP_FORMS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_IAP_FORMS_TOUR_HREF } from './focPlanningPTourRegistry';

export { FOC_IAP_FORMS_TOUR_HREF, focPlanningPTourNavVisible };

/**
 * 12.4 walk stays on Planning P: form lifecycle, Forms dropdown coverage, then
 * Approved ICS Forms (no navigation to the standalone /forms catalog page).
 */
export const FOC_IAP_FORMS_TOUR_STEPS: FocPlanningPTourStepDef[] = [
  {
    id: 'sectionIntro',
    phaseIndex: 6,
    title: '12.4 ICS Form Lifecycle, Coverage & Export',
    description:
      'This walkthrough covers the operational form lifecycle on Planning P, how to open any ICS form from the in-workspace Forms dropdown, and how Approved ICS Forms browses prior operational-period packages — without leaving the Planning P workflow for a separate forms catalog page.',
  },
  focPlanningPRequirement({
    id: 'iap005',
    requirementId: 'FOC-IAP-005',
    name: 'Operational Form Lifecycle Standard',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-form-card',
    widget: 'incidentObjectives',
    description:
      'For each Coast Guard ICS form implemented in PRATUS, the system supports authorized initiation, prepopulation from incident data, role-based contribution, draft/review/approval or signature when applicable, version control, operational-period association, source-faithful export, retention, and downstream data reuse.',
  }),
  focPlanningPRequirement({
    id: 'iap005a',
    requirementId: 'FOC-IAP-005a',
    name: 'Authoritative Field Mapping',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics202-card',
    widget: 'incidentObjectives',
    description:
      'Each implemented ICS form maintains a field-level mapping to the current approved USCG source form — official block numbers, names, sequence, headers/footers, instructions, and signature/prepared-by areas — to the fullest extent practicable.',
  }),
  focPlanningPRequirement({
    id: 'iap005b',
    requirementId: 'FOC-IAP-005b',
    name: 'Data Entered Once',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'When the same authoritative incident value appears in multiple forms or workspaces, PRATUS reuses the authoritative data rather than requiring independent duplicate entry. Approval and version rules prevent a later change from silently rewriting previously approved or published products.',
  }),
  focPlanningPRequirement({
    id: 'iap005c',
    requirementId: 'FOC-IAP-005c',
    name: 'Continuation Pages',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics204-card',
    widget: 'assignmentList',
    description:
      'When content exceeds the space available in an official form block, PRATUS creates a continuation page retaining the official header/footer and identifying the overflowing block (for example “[block number]. [block name] (continued)” or the approved equivalent) without duplicating content already shown on the source page.',
  }),
  focPlanningPRequirement({
    id: 'iap005d',
    requirementId: 'FOC-IAP-005d',
    name: 'Export Sanitation',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'Official exports do not contain raw AI citations, developer/test notes, placeholder variables, sample data, internal field names, unsupported narrative such as “no data recorded,” or other implementation artifacts unless that content is explicitly part of the approved source form.',
  }),
  focPlanningPRequirement({
    id: 'iap005e',
    requirementId: 'FOC-IAP-005e',
    name: 'Blank Fields and Prepared-By/Signature Integrity',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics202-card',
    widget: 'incidentObjectives',
    description:
      'Blank source-form fields remain blank in the correct location unless the official form requires other behavior. Prepared-by, signature, date/time, approval, and electronic-signature information appears in the correct official location and remains associated with the approved record/version.',
  }),
  focPlanningPRequirement({
    id: 'iap005f',
    requirementId: 'FOC-IAP-005f',
    name: 'Operational Period Consistency',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'working-op-indicator',
    description:
      'A centrally controlled incident operational period is applied consistently to applicable forms and products. Users can identify the operational period/version associated with every form and are protected from unintentionally editing a prior approved operational-period package via Working OP mode and Approved ICS Forms.',
  }),
  focPlanningPRequirement({
    id: 'iap006',
    requirementId: 'FOC-IAP-006',
    name: 'ICS Form Coverage Baseline',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-forms-dropdown',
    description:
      'Within Planning P, the Forms dropdown opens the Coast Guard ICS forms required by the applicable incident function. Search the list to open any implemented form in the workspace — conditional forms are available when needed without a separate catalog page.',
  }),
  focPlanningPRequirement({
    id: 'iap006a',
    requirementId: 'FOC-IAP-006a',
    name: 'Core IAP and Assignment Forms',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'FOC coverage includes, as applicable, ICS 201, 202, 203, 204, 204A, 205, 205A, 206, 207, 208, 208HM, 214, 215, 215A, 217A, 220/220UAS, and 234 — selectable from the Planning P Forms dropdown. Data relationships among organization, assignments, communications, medical, safety, resources, tactics, and operational period are preserved.',
  }),
  focPlanningPRequirement({
    id: 'iap006b',
    requirementId: 'FOC-IAP-006b',
    name: 'Situation, Action, and Meeting Products',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'FOC supports applicable situation/action/meeting products including ICS 209, ICS 230, ICS 232, ICS 232A, and ICS 233 when required by the incident workflow. ICS 230 derives from the incident meeting/battle rhythm; ICS 233 supports open-action continuity.',
  }),
  focPlanningPRequirement({
    id: 'iap006c',
    requirementId: 'FOC-IAP-006c',
    name: 'Resource and Accountability Forms',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'FOC supports applicable resource/accountability products including ICS 211, 211A, 213RR, 214, and ICS 261. T-card concepts support resource status in the Resources workspace rather than a full ICS 219 form product. ICS 213 General Message remains Government Validation Required if the approved Coast Guard source/workflow is not in the project baseline.',
  }),
  focPlanningPRequirement({
    id: 'iap006d',
    requirementId: 'FOC-IAP-006d',
    name: 'Demobilization and Personnel Forms',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'FOC supports applicable demobilization/personnel products including ICS 221, ICS 225, and ICS 236 and connects them to the demobilization lifecycle rather than implementing them as isolated exports.',
  }),
  focPlanningPRequirement({
    id: 'iap006e',
    requirementId: 'FOC-IAP-006e',
    name: 'Logistics, Facilities, and Safety Specialty Forms',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'FOC supports applicable specialty products including ICS 217A and ICS 235 connected to Communications and Facilities workflows. ICS 237 is not implemented as an in-platform form in the current baseline; this walkthrough notes the coverage gap without claiming a Safety specialty 237 product.',
  }),
  focPlanningPRequirement({
    id: 'iap006f',
    requirementId: 'FOC-IAP-006f',
    name: 'ICS 210 Validation Requirement',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'The program validates the current approved Coast Guard purpose, ownership, field structure, workflow, and FOC need for ICS 210 before developer implementation. Until validated, ICS 210 is Government Decision Required rather than an inferred build requirement.',
  }),
  focPlanningPRequirement({
    id: 'iap007',
    requirementId: 'FOC-IAP-007',
    name: 'Form Availability and Missing-Export Closure',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'approved-ics-forms',
    description:
      'Approved ICS Forms opens the read-only packages for the current and prior operational periods. From Planning P, use this control to browse approved forms without editing Working OP products. For each implemented form opened in the workspace, Preview/Export produces the approved PDF/Word output used for retention, briefing, and distribution.',
  }),
  {
    id: 'iap007export',
    phaseIndex: 6,
    tour: 'ics-form-export',
    widget: 'incidentObjectives',
    title: 'FOC-IAP-007 — Export from the open form',
    description:
      'While a form is open in Planning P, Preview / Export produces the official output for that product — closing the path from in-platform workflow to shareable form export without visiting a separate forms catalog page.',
  },
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_IAP_FORMS),
  },
];

export function focIapFormsTourStepIds(): string[] {
  return FOC_IAP_FORMS_TOUR_STEPS.map((step) => step.id);
}
